import { TFunction } from 'i18next';

import {
  ChallengeDatesType,
  ChallengeGoalTypes,
  ChallengePhaseType,
  ChallengesType,
  ChallengeTypeType,
} from 'interfaces';
import {
  CHALLENGE_PHASE,
  CHALLENGE,
  RESULT_STATUS,
} from 'constants/challenges';
import { getFormatedDates } from './date.helpers';

export const getChallengePhase = (dates: {
  [key: string]: {
    start: { raw: Date } | null;
    end: { raw: Date } | null;
  };
}): ChallengePhaseType | null => {
  const now = new Date();
  if (dates) {
    // DIFFUSION PERIOD
    if (dates.diffusion?.start?.raw && now < dates.diffusion.start.raw) {
      return CHALLENGE_PHASE.BEFORE_DIFFUSION;
    }
    if (dates.diffusion?.end?.raw && now >= dates.diffusion.end.raw) {
      return CHALLENGE_PHASE.AFTER_DIFFUSION;
    }
    // SIGN_IN PERIOD
    if (dates.signIn?.start?.raw && now < dates.signIn.start.raw) {
      return CHALLENGE_PHASE.BEFORE_SIGN_IN;
    }
    if (
      dates.signIn?.start?.raw &&
      dates.signIn?.end?.raw &&
      now >= dates.signIn.start.raw &&
      now < dates.signIn.end.raw
    ) {
      return CHALLENGE_PHASE.DURING_SIGN_IN;
    }
    // CHALLENGE PERIOD
    if (dates.challenge?.start?.raw && now < dates.challenge.start.raw) {
      return CHALLENGE_PHASE.BEFORE_CHALLENGE;
    }
    if (
      dates.challenge?.start?.raw &&
      now >= dates.challenge.start.raw &&
      (!dates.challenge.end ||
        (dates.challenge.end && now < dates.challenge.end.raw))
    ) {
      return CHALLENGE_PHASE.DURING_CHALLENGE;
    }
    if (dates.challenge?.end?.raw && now >= dates.challenge.end.raw) {
      return CHALLENGE_PHASE.AFTER_CHALLENGE;
    }
    // eslint-disable-next-line no-console
    console.error('Dates error, cannot calculate challenge phase');
    return null;
  }
  // eslint-disable-next-line no-console
  console.error('No dates given');
  return null;
};

export const getChallengeTranslation = (
  challengeType: ChallengeTypeType,
  t: TFunction,
): string => {
  const mappingTable = {
    [CHALLENGE.CLASSIC]: t('challenges.classic'),
    [CHALLENGE.DEAL]: t('challenges.deal'),
    [CHALLENGE.LIST]: t('challenges.list'),
    [CHALLENGE.PHOTO]: t('challenges.photo_contest'),
  };
  return mappingTable[challengeType];
};

export const sumGoalRewards = (
  goals: (ChallengeGoalTypes | undefined)[],
): number | null => {
  if (goals?.[0]?.challengeReward == null) {
    return null;
  }
  return goals.reduce(
    (acc, curr) => acc + (curr?.challengeReward?.value || 0),
    0,
  );
};

export const getStage = (
  challengeGoals: (ChallengeGoalTypes | undefined)[],
  resultValue: number,
): number =>
  challengeGoals.reduceRight((acc, curr, index) => {
    if (
      index === challengeGoals.length - 1 ||
      (curr?.value && resultValue < curr.value)
    ) {
      return index + 1;
    }
    return acc;
  }, 1);

type ChallengeDescriptionTitleType = {
  challenge: ChallengesType;
  hasResults: boolean;
  t: TFunction;
  language: string;
};

// See: https://loyaltyexpert.sharepoint.com/:x:/s/DEPARTEMENTPRODUITSSOLUTIONS/ETEspB1Qh5RJuFTxY9eJvKgBkLAx9QlkzrtTodZOeLMxtA?e=hWjSWb
export const getChallengeDescriptionTitle = ({
  challenge,
  hasResults,
  t,
  language,
}: ChallengeDescriptionTitleType) => {
  const dates: ChallengeDatesType = getFormatedDates(
    {
      challenge: {
        start: challenge.startDate,
        end: challenge.endDate,
      },
    },
    { day: 'numeric', month: 'short', year: 'numeric' },
    language,
  );
  const currentPhase = getChallengePhase(dates);

  if (currentPhase === CHALLENGE_PHASE.BEFORE_CHALLENGE) {
    return t('challenges.detail.description.declareResultsSoon');
  }

  if (currentPhase === CHALLENGE_PHASE.AFTER_CHALLENGE) {
    return !hasResults
      ? t('challenges.detail.description.noResults')
      : undefined;
  }

  const hasValidatedDeclaration = challenge.declarations?.some(
    (declaration) => declaration.status === RESULT_STATUS.VALIDATED,
  );
  if (challenge.isDeclarationMultiple) {
    return hasValidatedDeclaration
      ? t('challenges.detail.description.declareNewResults')
      : t('challenges.detail.description.declareResultsParticipate');
  }

  const hasNoDeclarations = !challenge.declarations?.length;
  const areAllDeclarationsRefused = challenge.declarations?.every(
    (declaration) => declaration.status === RESULT_STATUS.REFUSED,
  );
  if (hasNoDeclarations) {
    return t('challenges.detail.description.declareResultsParticipate');
  } else if (areAllDeclarationsRefused) {
    return t('challenges.detail.description.retryRefusedDeclaration');
  } else if (hasValidatedDeclaration) {
    return t('challenges.detail.description.alreadyDeclared');
  }
};
